import React, { useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ProductCard } from './components/ProductCard';
import { ProductMap } from './components/ProductMap';
import { MobileViewToggle } from './components/MobileViewToggle';
import { useProducts } from './hooks/use-products';
import { useIsMobile } from './hooks/use-is-mobile';
import { useStickyMap } from './hooks/use-sticky-map';

const MOBILE_MAP_HEIGHT = '100vh';
const DESKTOP_MAP_HEIGHT =
  'calc(100dvh - var(--portfolio-map-sticky-top, 120px) - 20px)';

const CardListSkeleton = () => (
  <div className="space-y-5">
    {Array.from({ length: 3 }).map((_, i) => (
      <div
        key={i}
        className="h-[28rem] animate-pulse rounded-md bg-slate-100"
      />
    ))}
  </div>
);

export const PortfolioMap = () => {
  const { products, isLoading } = useProducts();
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState('list');

  const sectionRef = useRef(null);
  const wrapperRef = useRef(null);

  useStickyMap({ sectionRef });

  const handleMobileViewChange = useCallback((nextView) => {
    if (nextView !== 'map' || !sectionRef.current) {
      setMobileView(nextView);
      return;
    }

    const nav = document.querySelector('.site-menu-navigation');
    const navTop = nav ? parseFloat(window.getComputedStyle(nav).top) || 0 : 0;
    const headerOffset = nav ? nav.offsetHeight + navTop : 0;
    const sectionTop =
      sectionRef.current.getBoundingClientRect().top + window.scrollY;
    const target = Math.max(0, sectionTop - headerOffset);

    flushSync(() => setMobileView(nextView));

    if (Math.abs(window.scrollY - target) > 2) {
      window.scrollTo({ top: target, behavior: 'smooth' });
    }
  }, []);

  const showList = !isMobile || mobileView === 'list';
  const showMap = !isMobile || mobileView === 'map';

  return (
    <section
      ref={sectionRef}
      className="relative flex min-h-[100dvh] flex-col bg-slate-50 lg:block lg:min-h-0"
    >
      <div className="flex w-full flex-col lg:flex-row">
        <div
          className={`${showList ? 'block' : 'hidden'} w-full p-5 lg:w-[35%] lg:shrink-0 lg:px-4 lg:py-4`}
        >
          {isLoading ? (
            <CardListSkeleton />
          ) : (
            <ul className="m-0 space-y-5">
              {products.map((product, i) => (
                <li key={product.id}>
                  <ProductCard product={product} index={i + 1} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div
          className={`${showMap ? 'block' : 'hidden'} w-full lg:block lg:w-[65%] lg:py-4 lg:pr-4`}
        >
          <div ref={wrapperRef} className="w-full lg:sticky" style={{ top: 'var(--portfolio-map-sticky-top, 120px)' }}>
            <div
              className="w-full overflow-hidden rounded-none lg:rounded-md"
              style={{ height: isMobile ? MOBILE_MAP_HEIGHT : DESKTOP_MAP_HEIGHT }}
            >
              {!isLoading && <ProductMap products={products} />}
            </div>
          </div>
        </div>
      </div>

      {isMobile && (
        <div className="sticky bottom-0 z-20 mt-auto flex bg-[#000000] px-5 py-2.5">
          <MobileViewToggle value={mobileView} onChange={handleMobileViewChange} />
        </div>
      )}
    </section>
  );
};
